/**
 * Puzzle Archive page component.
 * Displays historical puzzles that players can replay.
 * This component is game-agnostic and accepts configuration for game-specific logic.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../Store/store';
import { BasePageProps } from '../../Page/PageTypes';
import { GetTodaysDate, GetHumanReadableTimeUntilTomorrow, getSingleLevelScore, reportConversionEvent, EVENT_NAMES } from '@mental-mint/shared-utils';
import { LevelScore } from '@mental-mint/shared-types';
import * as Lucide from 'lucide-react';
import { useMediaQuery } from 'react-responsive'
import { ButtonStylized, ButtonStylizedType } from '../../ButtonStylized/ButtonStylized';

/**
 * Configuration for a single difficulty level in the archive
 */
export interface ArchiveDifficultyConfig {
  /** Unique identifier for this difficulty (e.g., "easy", "medium", "hard") */
  id: string;
  /** Display name for this difficulty (e.g., "Easy", "Medium", "Hard") */
  label: string;
}

/**
 * Props for the PagePuzzleArchive component.
 */
interface PagePuzzleArchiveProps extends BasePageProps {
  /** 
   * Callback when user selects puzzles to play.
   * Level IDs will be passed to start new gameplay session.
   */
  onPuzzlesSelected: (requestedLevelIds: string[]) => void;
  
  /**
   * Array of difficulty configurations to display (e.g., Easy, Medium, Hard)
   */
  difficulties: ArchiveDifficultyConfig[];
  
  /**
   * Function to generate a level ID for a given date and difficulty
   */
  getLevelId: (date: Date, difficultyId: string) => string;
  
  /**
   * Optional function to format duration in seconds to human-readable string
   * If not provided, duration won't be displayed
   */
  formatDuration?: (seconds: number) => string;
  
  /**
   * Optional function to get emoji/icon text for a level score
   * If not provided, no emoji will be displayed
   */
  getScoreEmoji?: (levelScore: LevelScore<any>) => string;
  
  /**
   * Optional function to get attempts text for a level score
   * If not provided, attempts won't be displayed
   */
  getAttemptsText?: (levelScore: LevelScore<any>) => string;
  
  /**
   * Optional function to get puzzle preview info (e.g., start/end words)
   * Returns object with content (ReactNode) and styles to show in the button
   */
  getPuzzlePreview?: (date: Date, difficultyId: string) => {
    content: React.ReactNode;
    styles?: React.CSSProperties;
  } | null;
  
  /**
   * Optional custom render function for level selection button
   * If provided, this will be used instead of the default button rendering
   * Should return a clickable element that calls onPlayPuzzle when clicked
   */
  renderLevelButton?: (
    date: Date,
    difficulty: ArchiveDifficultyConfig,
    levelScore: LevelScore<any> | undefined,
    onPlayPuzzle: (date: Date, difficultyId: string) => void
  ) => React.ReactNode;
}

export const PagePuzzleArchive: React.FC<PagePuzzleArchiveProps> = ({
  onPuzzlesSelected,
  difficulties,
  getLevelId,
  formatDuration,
  getScoreEmoji,
  getAttemptsText,
  getPuzzlePreview,
  renderLevelButton
}) => {
  const isNarrowScreen = useMediaQuery({ query: '(max-width: 760px)' })
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  const game = currentGame!;
  const [timeUntilTomorrow, setTimeUntilTomorrow] = useState(GetHumanReadableTimeUntilTomorrow());

  // Update time until tomorrow every second, but only when page is visible
  useEffect(() => {
      // Don't run interval if page is hidden (background tab)
      if (document.hidden) {
        return;
      }

      const intervalId = setInterval(() => {
          // Skip update if page becomes hidden
          if (document.hidden) {
            return;
          }
          setTimeUntilTomorrow(GetHumanReadableTimeUntilTomorrow());
      }, 1000);

      // Update immediately when page becomes visible
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          setTimeUntilTomorrow(GetHumanReadableTimeUntilTomorrow());
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        clearInterval(intervalId);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
  }, []);

  // Memoize today so that it doesn't change on every render.
  const today = useMemo(() => GetTodaysDate(), []);
  // Memoize cutoffDate based on the memoized today.
  // We only support storing data for the last week due to cookie size limits on browsers 
  // (usually 4096 bytes per cookie, which you get after about 22 level scores at time of writing on 4/11, which is 7 days).
  // We could always reduce size of level scores, but this is a good tradeoff for now.
  const cutoffDate = useMemo(() => {
    const date = new Date(today);
    date.setDate(date.getDate() - 7);
    return date;
  }, [today]);
  // Memoize dates so that they are recalculated only when 'today' or 'cutoffDate' changes.
  const dates = useMemo(() => {
    const dates = [];
    let currentDate = new Date(today);
    currentDate.setDate(currentDate.getDate() + 1);
    while (currentDate >= cutoffDate) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() - 1);
    }
    return dates;
  }, [today, cutoffDate]);

  // Use ISO strings for keys.
  // Completion data stores level scores for each difficulty per date
  const [completionData, setCompletionData] = useState<Record<string, Record<string, LevelScore<any> | undefined>>>({});

  useEffect(() => {
    const fetchCompletionData = async () => {
      const data: Record<string, Record<string, LevelScore<any> | undefined>> = {};
      for (const date of dates) {
        const key = date.toISOString();
        data[key] = {};
        
        // Fetch level scores for each difficulty
        for (const difficulty of difficulties) {
          const levelId = getLevelId(date, difficulty.id);
          data[key][difficulty.id] = await getSingleLevelScore(game, levelId);
        }
      }
      setCompletionData(data);
    };

    fetchCompletionData();
  }, [dates, difficulties, getLevelId, game.metadata.gameId]); // Only re-fetch when gameId changes, not entire game object

  const handlePlayPuzzle = (date: Date, difficultyId: string) => {
    const levelId = getLevelId(date, difficultyId);
    
    // Track archive puzzle selection (conversion event)
    reportConversionEvent(
      EVENT_NAMES.SELECT_ARCHIVED_PUZZLE,
      {},
      `Selected puzzle: ${levelId}`
    );
    
    onPuzzlesSelected([levelId]);
  };

  const getOrdinalSuffix = (day: number) => {
    if (day > 3 && day < 21) return day + "th";
    switch (day % 10) {
      case 1: return day + "st";
      case 2: return day + "nd";
      case 3: return day + "rd";
      default: return day + "th";
    }
  };

  // Format date as "Tue, March 18th". If it's today, append a colored "(today)".
  const renderDate = (date: Date) => {
    const weekday = date.toLocaleString('en-US', { weekday: 'short' });
    const month = date.toLocaleString('en-US', { month: 'long' });
    const day = date.getDate();
    const dateString = `${weekday}, ${month} ${getOrdinalSuffix(day)}`;

    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
        <span style={{fontSize:"1.3rem", fontFamily: game.fontPrimary, color: game.colorPrimary}}>
          {dateString}
        </span>
        { date.getTime() === today.getTime() && (
          <span className="today-label" style={{fontSize:"1.3rem", fontFamily: game.fontPrimary, color:"rgb(247,148,29)"}}>
            (today)
          </span>
        )}
      </div>
    );
  };

  const renderLevelSelection = (date: Date, difficulty: ArchiveDifficultyConfig, levelScore: LevelScore<any> | undefined) => {
    // Use custom render function if provided
    if (renderLevelButton) {
      return renderLevelButton(date, difficulty, levelScore, handlePlayPuzzle);
    }
    
    // Default rendering
    const icon = levelScore !== undefined && levelScore.isWon ? 
      <Lucide.SquareCheck size={30} strokeWidth={3} color='rgb(131,206,122)' /> : 
      <Lucide.Square size={30} strokeWidth={3} color='rgb(255, 255, 255)' />;
    
    const puzzlePreview = getPuzzlePreview ? getPuzzlePreview(date, difficulty.id) : null;
    
    return (
      <ButtonStylized 
          buttonType={levelScore && levelScore.isWon ? ButtonStylizedType.Secondary : ButtonStylizedType.Primary} 
          icon={icon} 
          onClick={() => handlePlayPuzzle(date, difficulty.id)}
          additionalStyles={{
            width: isNarrowScreen ? "100%" : "200px", 
            maxWidth: '200px',
            height: '80px',
            padding: '0.75rem',
            overflow: 'hidden'
          }}
      >
        {/* Inner button display */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left', gap: '0.3rem', width: '100%' }}>
          {/* Difficulty label */}
          <div style={{
            fontSize: "1.1rem",
            fontWeight: "normal",
            textAlign: "center",
            color: "inherit",
            fontFamily: game.fontPrimary
          }}>
            {difficulty.label}
          </div>
          
          {puzzlePreview && (
            <div style={{
              fontSize: "1rem",
              lineHeight: "1.3",
              textAlign: "left",
              color: "inherit",
              opacity: 0.95,
              ...puzzlePreview.styles
            }}>
              {puzzlePreview.content}
            </div>
          )}
          {levelScore && (
            <div style={{ fontFamily: game.fontSecondary, fontSize: "0.8rem", color: game.colorSecondary, opacity: 0.8 }}>
              {formatDuration && levelScore.isWon && levelScore.originalTimeSec != null && <div style={{ fontSize: "0.9rem" }}>
                {/* Time taken only shown if puzzle completed with a given time */}
                {`Time: ${formatDuration(levelScore.originalTimeSec)}`}
              </div>}
              {/* Num attempts always shown if formatter provided */}
              {(getScoreEmoji || getAttemptsText) && (
                <div style={{ color: game.colorSecondary, opacity: 0.8 }}>
                  {getScoreEmoji && (
                    <span style={{ textShadow: "1px 0px 0px rgba(0,0,0,0.2), -1px 0px 0px rgba(0,0,0,0.2), 0px 1px 0px rgba(0,0,0,0.2), 0px -1px 0px rgba(0,0,0,0.2)" }}>
                      {getScoreEmoji(levelScore)}
                    </span>
                  )}
                  {getAttemptsText && <span> {getAttemptsText(levelScore)}</span>}
                </div>
              )}
            </div>
          )}
        </div>
      </ButtonStylized>
    );
  };

  return (
      <div className="archive-list" style={{
        display: 'flex', flexDirection: 'column', gap: '2rem', 
        alignItems: 'center', width: '100%',
        padding: '1rem', boxSizing: 'border-box'
      }}>
        {dates.map((date, index) => {
          const key = date.toISOString();
          return (
            <div key={index} className="archive-row" 
              style={{
                display:"flex", 
                flexDirection:"column", 
                alignItems:"center",
                boxSizing:"border-box",
                width: '100%',
              }}>
              <div style={{fontFamily:game.fontPrimary, color: game.colorPrimary, marginTop:(isNarrowScreen ? "0.7rem" : "0"), marginBottom:"1rem"}}>
                {renderDate(date)}
              </div>
              {completionData[key] && index === 0 && (
                // Tomorrow's puzzles
                <div style={{ fontSize: "1rem", fontFamily:game.fontSecondary, color: game.colorSecondary, opacity: 0.6, fontStyle: 'italic',
                      display:"flex", flexDirection:"row", alignItems:"center", gap: "5px" }}>
                  Coming out in {timeUntilTomorrow}
                </div>
              )}
              {completionData[key] && index > 0 && (
                // Today and prior puzzles
                  <div 
                    className="difficulty-selection-container" 
                    style={{ 
                      width: '100%', 
                      display: 'flex',
                      flexDirection: isNarrowScreen ? 'column' : 'row',
                      gap: '10px',
                      justifyContent: 'center',
                    }}
                  >
                    {difficulties.map((difficulty) => (
                      <React.Fragment key={difficulty.id}>
                        {renderLevelSelection(date, difficulty, completionData[key][difficulty.id])}
                      </React.Fragment>
                    ))}
                  </div>
              )}
            </div>
          );
        })}
      </div>
  );
};